import { useEffect, useState, type ReactNode } from 'react';
import type {
  CommunityCanvas,
  CommunityLogoStyle,
  CommunityShape,
  CommunityShapeKind,
  CommunityTextBox,
  CommunityTextRole,
  CommunityZonesV2,
} from '../../types/api';
import {
  BoxFields,
  BuilderGrid,
  BuilderHint,
  BuilderIcon,
  BuilderMore,
  BuilderSection,
  ColorField,
  IconButton,
  NumberField,
  RangeField,
  Segmented,
  SelectField,
  Switch,
  TextField,
} from './BuilderControls';
import { findShape, findText, layerName, type LayerKey } from './builderLayers';
import {
  AI_HINT_MAX,
  AI_MIN_CHARS,
  CANVAS_PX,
  LABEL_MAX_LENGTH,
  LINE_SPACING_MAX,
  LINE_SPACING_MIN,
  LOGO_MINIMUM,
  LOGO_STYLE_LABELS,
  MAX_CHARS_MAX,
  MAX_CHARS_MIN,
  MAX_LINES_MAX,
  MAX_LINES_MIN,
  OPACITY_MIN,
  PORTRAIT_MAX_D,
  PORTRAIT_MIN_D,
  RADIUS_MAX,
  SHAPE_KIND_LABELS,
  SHAPE_MINIMUM,
  SIZE_MAX_MAX,
  SIZE_MAX_MIN,
  SIZE_MIN_MIN,
  TEXT_MINIMUM,
  TEXT_ROLES,
  TEXT_ROLE_ORDER,
  THICKNESS_MAX,
  THICKNESS_MIN,
  TRACKING_MAX,
  TRACKING_MIN,
  VALUE_MINIMUM,
  clampPortrait,
  isAiBox,
  type BrandPalette,
} from './communityZones';
import { fontFamilyCss, fontWeightFor, loadCatalogFont } from './fontCatalog';
import { FontGallery } from './FontGallery';
import { useFontCatalog } from './useFontCatalog';

/** The eight gradient directions, as CSS reads the angle: 0 points up. */
const ANGLES = [
  { value: '0', label: '↑', title: 'Toward the top (0 degrees)' },
  { value: '45', label: '↗', title: 'Toward the top right (45 degrees)' },
  { value: '90', label: '→', title: 'Toward the right (90 degrees)' },
  { value: '135', label: '↘', title: 'Toward the bottom right (135 degrees)' },
  { value: '180', label: '↓', title: 'Toward the bottom (180 degrees)' },
  { value: '225', label: '↙', title: 'Toward the bottom left (225 degrees)' },
  { value: '270', label: '←', title: 'Toward the left (270 degrees)' },
  { value: '315', label: '↖', title: 'Toward the top left (315 degrees)' },
] as const;

type BackdropKind = CommunityCanvas['kind'];

/** Who writes a text box: the member in the brief, or the AI at design time. */
const FILL_OPTIONS = [
  { value: 'user', label: 'Member', title: 'The member types it in when creating a post' },
  { value: 'ai', label: 'AI', title: 'AI writes it unless the member types their own' },
] as const;

const BACKDROP_KINDS = [
  { value: 'image', label: 'Image', title: 'Your own finished artwork' },
  { value: 'color', label: 'Colour' },
  { value: 'gradient', label: 'Gradient' },
] as const;

const WEIGHTS = [
  { value: 'bold', label: 'Bold' },
  { value: 'regular', label: 'Regular' },
] as const;

const ALIGNS = [
  { value: 'left', label: <BuilderIcon name="align-left" />, title: 'Align left' },
  { value: 'center', label: <BuilderIcon name="align-center" />, title: 'Align centre' },
  { value: 'right', label: <BuilderIcon name="align-right" />, title: 'Align right' },
] as const;

const VALIGNS = [
  { value: 'top', label: <BuilderIcon name="valign-top" />, title: 'Sit at the top' },
  { value: 'middle', label: <BuilderIcon name="valign-middle" />, title: 'Sit in the middle' },
  { value: 'bottom', label: <BuilderIcon name="valign-bottom" />, title: 'Sit at the bottom' },
] as const;

const SHAPE_KINDS: readonly { value: CommunityShapeKind; label: string }[] = [
  { value: 'rect', label: SHAPE_KIND_LABELS.rect },
  { value: 'ellipse', label: SHAPE_KIND_LABELS.ellipse },
  { value: 'line', label: SHAPE_KIND_LABELS.line },
];

const LOGO_STYLES = (Object.keys(LOGO_STYLE_LABELS) as CommunityLogoStyle[]).map((style) => ({
  value: style,
  label: LOGO_STYLE_LABELS[style].label,
  title: LOGO_STYLE_LABELS[style].blurb,
}));

const TEXT_ROLE_OPTIONS = (hasOtherHeadline: boolean) =>
  TEXT_ROLE_ORDER.map((role) => ({
    value: role,
    label: TEXT_ROLES[role].label,
    disabled: role === 'headline' && hasOtherHeadline,
  }));

/** The canvas drag is the main way to place things; the numbers start folded. */
const POSITION_TITLE = 'Position and size';

export interface CommunityLayerInspectorProps {
  zones: CommunityZonesV2;
  layer: LayerKey;
  palette: BrandPalette;
  disabled: boolean;
  imageSource: ReactNode;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onChange: (zones: CommunityZonesV2) => void;
  onCanvasChange: (canvas: CommunityCanvas) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onReorder: (towardTop: boolean) => void;
}

/**
 * The inspector: every setting of the one layer in hand, in collapsible
 * sections the way an author thinks about it — what it says, how it is set,
 * what colour it is, where it sits. Every field writes through to the map at
 * once, so the canvas beside it is always the truth of the form.
 */
export function CommunityLayerInspector({
  zones,
  layer,
  palette,
  disabled,
  imageSource,
  canMoveUp,
  canMoveDown,
  onChange,
  onCanvasChange,
  onDuplicate,
  onRemove,
  onReorder,
}: CommunityLayerInspectorProps) {
  const text = findText(zones, layer);
  const shape = findShape(zones, layer);
  const isListLayer = text !== null || shape !== null;
  const isLastText = text !== null && zones.texts.length <= 1;

  function patchText(id: string, changes: Partial<CommunityTextBox>) {
    onChange({
      ...zones,
      texts: zones.texts.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)),
    });
  }

  function patchShape(id: string, changes: Partial<CommunityShape>) {
    onChange({
      ...zones,
      shapes: zones.shapes.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)),
    });
  }

  const kindLabel =
    layer === 'backdrop'
      ? 'Backdrop'
      : text
        ? `Text · ${TEXT_ROLES[text.role].label}`
        : shape
          ? 'Shape'
          : layer === 'logo'
            ? 'Logo'
            : layer === 'portrait'
              ? 'Photo'
              : 'Value';

  return (
    <div className="builder-inspector">
      <div className="builder-inspector__head">
        <div className="builder-inspector__title-block">
          <span className="builder-inspector__kind">{kindLabel}</span>
          <span className="builder-inspector__name">{layerName(zones, layer)}</span>
        </div>
        {isListLayer ? (
          <div className="builder-inspector__actions">
            <IconButton
              icon="up"
              label={shape ? 'Bring forward' : 'Move up the list'}
              disabled={disabled || !canMoveUp}
              onClick={() => onReorder(true)}
            />
            <IconButton
              icon="down"
              label={shape ? 'Send backward' : 'Move down the list'}
              disabled={disabled || !canMoveDown}
              onClick={() => onReorder(false)}
            />
            <IconButton
              icon="duplicate"
              label="Duplicate"
              disabled={disabled}
              onClick={onDuplicate}
            />
            <IconButton
              icon="delete"
              label="Delete"
              tone="danger"
              disabled={disabled || isLastText}
              title={isLastText ? 'A design keeps at least one text box.' : undefined}
              onClick={onRemove}
            />
          </div>
        ) : layer === 'portrait' || layer === 'value' ? (
          <div className="builder-inspector__actions">
            <IconButton
              icon="delete"
              label={layer === 'portrait' ? 'Remove the photo' : 'Remove the value box'}
              tone="danger"
              disabled={disabled}
              onClick={onRemove}
            />
          </div>
        ) : null}
      </div>

      {layer === 'backdrop' ? (
        <BackdropInspector
          canvas={zones.canvas}
          palette={palette}
          disabled={disabled}
          imageSource={imageSource}
          onChange={onCanvasChange}
        />
      ) : null}

      {text ? (
        <TextInspector
          text={text}
          hasOtherHeadline={zones.texts.some(
            (entry) => entry.role === 'headline' && entry.id !== text.id,
          )}
          palette={palette}
          disabled={disabled}
          onPatch={(changes) => patchText(text.id, changes)}
        />
      ) : null}

      {shape ? (
        <ShapeInspector
          shape={shape}
          palette={palette}
          disabled={disabled}
          onPatch={(changes) => patchShape(shape.id, changes)}
        />
      ) : null}

      {layer === 'logo' ? (
        <>
          <BuilderSection title="Logo" disabled={disabled}>
            <Switch
              label="Show the member's logo"
              title="Off leaves the design without a logo. Members can still switch theirs off per post."
              checked={zones.logo.enabled}
              disabled={disabled}
              onChange={(enabled) => onChange({ ...zones, logo: { ...zones.logo, enabled } })}
            />
            {zones.logo.enabled ? (
              <>
                <Segmented
                  label="Style"
                  value={zones.logo.style}
                  options={LOGO_STYLES}
                  disabled={disabled}
                  onChange={(style) => onChange({ ...zones, logo: { ...zones.logo, style } })}
                />
                <BuilderHint>{LOGO_STYLE_LABELS[zones.logo.style].blurb}</BuilderHint>
              </>
            ) : null}
          </BuilderSection>
          {zones.logo.enabled ? (
            <BuilderSection title={POSITION_TITLE} defaultOpen={false} disabled={disabled}>
              <BoxFields
                box={zones.logo.box}
                minimum={LOGO_MINIMUM}
                disabled={disabled}
                onChange={(box) => onChange({ ...zones, logo: { ...zones.logo, box } })}
              />
              <BuilderHint>Fitted inside the box, never stretched.</BuilderHint>
            </BuilderSection>
          ) : null}
        </>
      ) : null}

      {layer === 'portrait' && zones.portrait ? (
        <BuilderSection title={POSITION_TITLE} disabled={disabled}>
          <BuilderHint>A round photo the member picks when creating a post.</BuilderHint>
          <BuilderGrid>
            <NumberField
              label="Centre X"
              unit="%"
              min={0}
              max={100}
              value={Math.round(zones.portrait.cx * 1000) / 10}
              disabled={disabled}
              onCommit={(value) => {
                const current = zones.portrait;
                if (current) {
                  onChange({ ...zones, portrait: clampPortrait({ ...current, cx: value / 100 }) });
                }
              }}
            />
            <NumberField
              label="Centre Y"
              unit="%"
              min={0}
              max={100}
              value={Math.round(zones.portrait.cy * 1000) / 10}
              disabled={disabled}
              onCommit={(value) => {
                const current = zones.portrait;
                if (current) {
                  onChange({ ...zones, portrait: clampPortrait({ ...current, cy: value / 100 }) });
                }
              }}
            />
            <NumberField
              label="Diameter"
              title="Across the design"
              unit="%"
              min={PORTRAIT_MIN_D * 100}
              max={PORTRAIT_MAX_D * 100}
              value={Math.round(zones.portrait.d * 1000) / 10}
              disabled={disabled}
              onCommit={(value) => {
                const current = zones.portrait;
                if (current) {
                  onChange({ ...zones, portrait: clampPortrait({ ...current, d: value / 100 }) });
                }
              }}
            />
          </BuilderGrid>
        </BuilderSection>
      ) : null}

      {layer === 'value' && zones.value ? (
        <BuilderSection title={POSITION_TITLE} disabled={disabled}>
          <BuilderHint>A price or number the member types, in their accent colour.</BuilderHint>
          <BoxFields
            box={zones.value}
            minimum={VALUE_MINIMUM}
            disabled={disabled}
            onChange={(value) => onChange({ ...zones, value })}
          />
        </BuilderSection>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface BackdropInspectorProps {
  canvas: CommunityCanvas;
  palette: BrandPalette;
  disabled: boolean;
  imageSource: ReactNode;
  onChange: (canvas: CommunityCanvas) => void;
}

function BackdropInspector({
  canvas,
  palette,
  disabled,
  imageSource,
  onChange,
}: BackdropInspectorProps) {
  function switchKind(kind: BackdropKind) {
    if (kind === canvas.kind) return;
    // The colours carry across a switch, so trying gradient and going back to
    // a colour does not lose the one that was picked.
    const first =
      canvas.kind === 'color' ? canvas.color : canvas.kind === 'gradient' ? canvas.from : null;
    if (kind === 'image') onChange({ kind: 'image' });
    if (kind === 'color') onChange({ kind: 'color', color: first ?? 'brand:primary' });
    if (kind === 'gradient') {
      onChange({
        kind: 'gradient',
        from: first ?? 'brand:primary',
        to: 'brand:secondary',
        angle: 135,
      });
    }
  }

  return (
    <BuilderSection title="Fill" disabled={disabled}>
      <Segmented
        label="Backdrop"
        value={canvas.kind}
        options={BACKDROP_KINDS}
        disabled={disabled}
        onChange={switchKind}
      />

      {canvas.kind === 'image' ? (
        <>
          <BuilderHint>Cover-cropped to a square. Shapes and text still go on top.</BuilderHint>
          {imageSource}
        </>
      ) : null}

      {canvas.kind === 'color' ? (
        <ColorField
          label="Colour"
          value={canvas.color}
          palette={palette}
          disabled={disabled}
          onChange={(color) => onChange({ kind: 'color', color })}
        />
      ) : null}

      {canvas.kind === 'gradient' ? (
        <>
          <ColorField
            label="From"
            value={canvas.from}
            palette={palette}
            disabled={disabled}
            onChange={(from) => onChange({ ...canvas, from })}
          />
          <ColorField
            label="To"
            value={canvas.to}
            palette={palette}
            disabled={disabled}
            onChange={(to) => onChange({ ...canvas, to })}
          />
          <Segmented
            label="Direction"
            note={`${canvas.angle} degrees`}
            value={String(canvas.angle)}
            options={ANGLES}
            disabled={disabled}
            onChange={(angle) => onChange({ ...canvas, angle: Number(angle) })}
          />
          <button
            className="builder-text-button"
            type="button"
            disabled={disabled}
            onClick={() => onChange({ ...canvas, from: canvas.to, to: canvas.from })}
          >
            <BuilderIcon name="swap" />
            Swap colours
          </button>
        </>
      ) : null}
    </BuilderSection>
  );
}

/* ------------------------------------------------------------------ */

interface TextInspectorProps {
  text: CommunityTextBox;
  hasOtherHeadline: boolean;
  palette: BrandPalette;
  disabled: boolean;
  onPatch: (changes: Partial<CommunityTextBox>) => void;
}

function TextInspector({ text, hasOtherHeadline, palette, disabled, onPatch }: TextInspectorProps) {
  const isAi = isAiBox(text);
  const contentHint = isAi
    ? 'AI writes this box. The default text is its example, and the fallback.'
    : text.role === 'headline'
      ? 'Left empty, the headline falls back to the topic or caption.'
      : null;

  return (
    <>
      <BuilderSection title="Content" disabled={disabled}>
        <BuilderGrid>
          <SelectField
            label="Role"
            value={text.role}
            options={TEXT_ROLE_OPTIONS(hasOtherHeadline)}
            onChange={(role: CommunityTextRole) => onPatch({ role })}
          />
          <TextField
            label="Field label"
            title={`What members see above this box's field when creating a post. Field id: ${text.id}`}
            maxLength={LABEL_MAX_LENGTH}
            value={text.label}
            onChange={(label) => onPatch({ label })}
          />
        </BuilderGrid>

        <Segmented
          label="Filled by"
          value={isAi ? 'ai' : 'user'}
          options={FILL_OPTIONS}
          disabled={disabled}
          onChange={(fill) =>
            onPatch(
              // The AI needs a little room to write in; a box that allowed
              // fewer characters is opened up to the minimum as it switches.
              fill === 'ai' && text.max_chars < AI_MIN_CHARS
                ? { fill, max_chars: AI_MIN_CHARS }
                : { fill },
            )
          }
        />

        {isAi ? (
          <TextField
            label="AI instructions"
            title="Optional: what this box should say, in a few words"
            rows={2}
            maxLength={AI_HINT_MAX}
            counterLimit={AI_HINT_MAX}
            placeholder="e.g. a punchy 3-word offer"
            value={text.ai_hint ?? ''}
            onChange={(aiHint) => onPatch({ ai_hint: aiHint })}
          />
        ) : null}

        <TextField
          label="Default text"
          title={
            isAi
              ? 'The example AI follows, and what shows if AI cannot fill it'
              : 'Prefilled when creating a post; members can type over it'
          }
          rows={2}
          counterLimit={text.max_chars}
          value={text.default}
          onChange={(value) => onPatch({ default: value })}
        />
        <BuilderGrid>
          <NumberField
            label="Character limit"
            title={isAi ? 'Most characters in this box' : 'Most characters a member may type'}
            min={isAi ? AI_MIN_CHARS : MAX_CHARS_MIN}
            max={MAX_CHARS_MAX}
            value={text.max_chars}
            disabled={disabled}
            onCommit={(value) => onPatch({ max_chars: Math.round(value) })}
          />
        </BuilderGrid>
        {contentHint ? <BuilderHint>{contentHint}</BuilderHint> : null}
      </BuilderSection>

      <BuilderSection title="Type" disabled={disabled}>
        <FontRow text={text} disabled={disabled} onPatch={onPatch} />
        <Segmented
          label="Weight"
          value={text.weight}
          options={WEIGHTS}
          disabled={disabled}
          onChange={(weight) => onPatch({ weight })}
        />
        <BuilderGrid>
          <NumberField
            label="Largest"
            title={`Size on the ${CANVAS_PX}px square. Long text steps down toward the smallest before it wraps.`}
            unit="px"
            min={SIZE_MAX_MIN}
            max={SIZE_MAX_MAX}
            value={text.size_max}
            disabled={disabled}
            onCommit={(value) => {
              const sizeMax = Math.round(value);
              onPatch({ size_max: sizeMax, size_min: Math.min(text.size_min, sizeMax) });
            }}
          />
          <NumberField
            label="Smallest"
            title={`Size on the ${CANVAS_PX}px square that long text may step down to.`}
            unit="px"
            min={SIZE_MIN_MIN}
            max={text.size_max}
            value={text.size_min}
            disabled={disabled}
            onCommit={(value) => onPatch({ size_min: Math.round(value) })}
          />
          <NumberField
            label="Lines"
            title="Most lines before the text stops wrapping"
            min={MAX_LINES_MIN}
            max={MAX_LINES_MAX}
            value={text.max_lines}
            disabled={disabled}
            onCommit={(value) => onPatch({ max_lines: Math.round(value) })}
          />
        </BuilderGrid>
        <BuilderMore label="More type settings">
          <BuilderGrid>
            <NumberField
              label="Letter spacing"
              unit="px"
              min={TRACKING_MIN}
              max={TRACKING_MAX}
              value={text.tracking}
              disabled={disabled}
              onCommit={(value) => onPatch({ tracking: Math.round(value) })}
            />
            <NumberField
              label="Line spacing"
              min={LINE_SPACING_MIN}
              max={LINE_SPACING_MAX}
              step={0.05}
              value={text.line_spacing}
              disabled={disabled}
              onCommit={(value) => onPatch({ line_spacing: Math.round(value * 100) / 100 })}
            />
          </BuilderGrid>
          <Switch
            label="Set in capitals"
            checked={text.uppercase}
            disabled={disabled}
            onChange={(uppercase) => onPatch({ uppercase })}
          />
        </BuilderMore>
      </BuilderSection>

      <BuilderSection title="Colour" disabled={disabled}>
        <ColorField
          label="Text colour"
          value={text.color}
          palette={palette}
          allowTextKeywords
          disabled={disabled}
          onChange={(color) => onPatch({ color })}
        />
        {text.color !== 'auto' ? (
          <BuilderHint>Pinned: drawn as set, with no panel behind it.</BuilderHint>
        ) : null}
      </BuilderSection>

      <BuilderSection title={POSITION_TITLE} defaultOpen={false} disabled={disabled}>
        <Segmented
          label="Across"
          value={text.align}
          options={ALIGNS}
          disabled={disabled}
          onChange={(align) => onPatch({ align })}
        />
        <Segmented
          label="Down"
          value={text.valign}
          options={VALIGNS}
          disabled={disabled}
          onChange={(valign) => onPatch({ valign })}
        />
        <BoxFields
          box={text.box}
          minimum={TEXT_MINIMUM}
          disabled={disabled}
          onChange={(box) => onPatch({ box })}
        />
      </BuilderSection>
    </>
  );
}

/* ------------------------------------------------------------------ */

interface FontRowProps {
  text: CommunityTextBox;
  disabled: boolean;
  onPatch: (changes: Partial<CommunityTextBox>) => void;
}

/**
 * The Font row: the box's face named in itself, opening the gallery. A slug
 * the catalogue has not answered for yet shows as written; one it no longer
 * offers says so, and the studio's problem list asks for another.
 */
function FontRow({ text, disabled, onPatch }: FontRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const catalog = useFontCatalog();
  const slug = text.font ?? null;
  const font = slug !== null ? (catalog.bySlug.get(slug) ?? null) : null;

  useEffect(() => {
    if (font) loadCatalogFont(font);
  }, [font]);

  const name =
    slug === null
      ? 'Brand font'
      : font
        ? font.name
        : catalog.status === 'ready'
          ? `${slug} (not offered)`
          : slug;

  return (
    <div className="builder-field">
      <span className="builder-label" aria-hidden="true">
        Font
      </span>
      <button
        className="builder-font-button"
        type="button"
        aria-haspopup="dialog"
        aria-label={`Font: ${name}. Choose another`}
        disabled={disabled}
        onClick={() => setIsOpen(true)}
      >
        <span
          className="builder-font-button__name"
          style={
            font
              ? { fontFamily: fontFamilyCss(font), fontWeight: fontWeightFor(font, text.weight) }
              : undefined
          }
        >
          {name}
        </span>
        <span className="builder-font-button__action" aria-hidden="true">
          Change
        </span>
      </button>
      {isOpen ? (
        <FontGallery
          text={text}
          catalog={catalog}
          onSelect={(next) => onPatch({ font: next })}
          onDismiss={() => setIsOpen(false)}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface ShapeInspectorProps {
  shape: CommunityShape;
  palette: BrandPalette;
  disabled: boolean;
  onPatch: (changes: Partial<CommunityShape>) => void;
}

function ShapeInspector({ shape, palette, disabled, onPatch }: ShapeInspectorProps) {
  return (
    <>
      <BuilderSection title="Shape" disabled={disabled}>
        <Segmented
          label="Kind"
          value={shape.kind}
          options={SHAPE_KINDS}
          disabled={disabled}
          onChange={(kind) => onPatch({ kind })}
        />
        <ColorField
          label="Fill"
          value={shape.fill}
          palette={palette}
          disabled={disabled}
          onChange={(fill) => onPatch({ fill })}
        />
        <RangeField
          label="Opacity"
          min={OPACITY_MIN * 100}
          max={100}
          step={5}
          value={Math.round(shape.opacity * 100)}
          readout={`${Math.round(shape.opacity * 100)}%`}
          onChange={(value) => onPatch({ opacity: value / 100 })}
        />
        {shape.kind === 'rect' ? (
          <RangeField
            label="Corner radius"
            min={0}
            max={RADIUS_MAX * 100}
            step={1}
            value={Math.round(shape.radius * 100)}
            readout={`${Math.round(shape.radius * 100)}%`}
            onChange={(value) => onPatch({ radius: value / 100 })}
          />
        ) : null}
        {shape.kind === 'line' ? (
          <RangeField
            label="Thickness"
            min={THICKNESS_MIN * 1000}
            max={THICKNESS_MAX * 1000}
            step={1}
            value={Math.round(shape.thickness * 1000)}
            readout={`${Math.round(shape.thickness * CANVAS_PX)}px`}
            onChange={(value) => onPatch({ thickness: value / 1000 })}
          />
        ) : null}
      </BuilderSection>
      <BuilderSection title={POSITION_TITLE} defaultOpen={false} disabled={disabled}>
        <BoxFields
          box={shape.box}
          minimum={SHAPE_MINIMUM}
          disabled={disabled}
          onChange={(box) => onPatch({ box })}
        />
        {shape.kind === 'line' ? (
          <BuilderHint>A line runs along the longer side of its box.</BuilderHint>
        ) : null}
      </BuilderSection>
    </>
  );
}
